/**
 * System Configuration Page
 * 
 * Manage system-wide settings, default configurations, and global parameters.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import Grid from '@mui/material/GridLegacy';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  CardContent,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack,
  Settings,
  Save,
  Refresh,
  ExpandMore,
  Email,
  Storage,
  Security,
  Notifications,
  Palette,
  Language,
  Shield,
  AccountBalance,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';

const SystemConfig = () => {
  const navigate = useNavigate();
  const { api, isAuthenticated, loading: authLoading } = useSuperAdmin();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Payment gateway state
  const [paymentConfig, setPaymentConfig] = useState({
    jazzcash_enabled: false,
    jazzcash_sandbox_mode: true,
    jazzcash_merchant_id: '',
    jazzcash_password: '',
    jazzcash_hash_key: '',
    jazzcash_return_url: '',
  });
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const [showSecrets, setShowSecrets] = useState({
    jazzcash_password: false,
    jazzcash_hash_key: false,
  });

  const [config, setConfig] = useState({
    // General Settings
    system_name: 'RTA/PTA Transport System',
    system_tagline: 'Regional Transport Authority Management',
    maintenance_mode: false,

    // Email Settings
    email_enabled: true,
    email_from_name: 'Transport Authority',
    email_from_address: 'noreply@transport.gov',

    // Security Settings
    session_timeout_minutes: 480,
    max_login_attempts: 5,
    lockout_duration_minutes: 30,
    require_2fa_for_admins: false,

    // Notification Settings
    notify_on_permit_create: true,
    notify_on_permit_approve: true,
    notify_on_permit_reject: true,
    notify_on_chalan_create: true,

    // Feature Flags
    enable_mobile_app: true,
    enable_qr_verification: true,
    enable_document_upload: true,
    enable_online_payment: false,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/superadmin/login');
      return;
    }
    fetchConfig();
    fetchPaymentConfig();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/superadmin/system-config/');
      // API returns [{key, value, value_type, ...}] — convert to flat object
      const configMap = {};
      response.data.forEach(item => {
        let val = item.value;
        if (item.value_type === 'boolean') {
          val = item.value === 'true' || item.value === 'True' || item.value === '1';
        } else if (item.value_type === 'integer') {
          val = parseInt(item.value, 10) || 0;
        } else if (item.value_type === 'float') {
          val = parseFloat(item.value) || 0;
        }
        configMap[item.key] = val;
      });
      // Merge so missing keys keep their UI defaults
      setConfig(prev => ({ ...prev, ...configMap }));
      setLoading(false);
    } catch (err) {
      setError('Failed to load system configuration');
      setLoading(false);
    }
  };

  const fetchPaymentConfig = async () => {
    try {
      const response = await api.get('/api/superadmin/payment-gateway/');
      setPaymentConfig(prev => ({ ...prev, ...response.data }));
    } catch (err) {
      // Non-fatal: defaults remain in place
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/api/superadmin/system-config/', { configs: config });
      setSuccess('Configuration saved successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handlePaymentChange = (field, value) => {
    setPaymentConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSavePayment = async () => {
    setPaymentSaving(true);
    setPaymentError('');
    setPaymentSuccess('');
    try {
      await api.put('/api/superadmin/payment-gateway/', paymentConfig);
      setPaymentSuccess('Payment gateway configuration saved successfully');
    } catch (err) {
      setPaymentError(
        err.response?.data?.error || 'Failed to save payment gateway configuration'
      );
    } finally {
      setPaymentSaving(false);
    }
  };

  const toggleSecretVisibility = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      }}>
        <CircularProgress sx={{ color: '#818cf8' }} size={48} />
        <Typography sx={{ mt: 2, color: '#94a3b8' }}>Loading configuration...</Typography>
      </Box>
    );
  }

  const configSections = [
    {
      title: 'General Settings',
      icon: <Settings />,
      color: '#6366f1',
      fields: [
        { key: 'system_name', label: 'System Name', type: 'text' },
        { key: 'system_tagline', label: 'System Tagline', type: 'text' },
        { key: 'maintenance_mode', label: 'Maintenance Mode', type: 'switch', description: 'Enable to show maintenance page to users' },
      ]
    },
    {
      title: 'Email Configuration',
      icon: <Email />,
      color: '#10b981',
      fields: [
        { key: 'email_enabled', label: 'Enable Email Notifications', type: 'switch' },
        { key: 'email_from_name', label: 'From Name', type: 'text' },
        { key: 'email_from_address', label: 'From Email Address', type: 'text' },
      ]
    },
    {
      title: 'Security Settings',
      icon: <Security />,
      color: '#f59e0b',
      fields: [
        { key: 'session_timeout_minutes', label: 'Session Timeout (minutes)', type: 'number' },
        { key: 'max_login_attempts', label: 'Max Login Attempts', type: 'number' },
        { key: 'lockout_duration_minutes', label: 'Lockout Duration (minutes)', type: 'number' },
        { key: 'require_2fa_for_admins', label: 'Require 2FA for Admins', type: 'switch' },
      ]
    },
    {
      title: 'Notification Preferences',
      icon: <Notifications />,
      color: '#ec4899',
      fields: [
        { key: 'notify_on_permit_create', label: 'Notify on Permit Creation', type: 'switch' },
        { key: 'notify_on_permit_approve', label: 'Notify on Permit Approval', type: 'switch' },
        { key: 'notify_on_permit_reject', label: 'Notify on Permit Rejection', type: 'switch' },
        { key: 'notify_on_chalan_create', label: 'Notify on Chalan Creation', type: 'switch' },
      ]
    },
    {
      title: 'Feature Flags',
      icon: <Shield />,
      color: '#3b82f6',
      fields: [
        { key: 'enable_mobile_app', label: 'Enable Mobile App Access', type: 'switch' },
        { key: 'enable_qr_verification', label: 'Enable QR Code Verification', type: 'switch' },
        { key: 'enable_document_upload', label: 'Enable Document Upload', type: 'switch' },
        { key: 'enable_online_payment', label: 'Enable Online Payment', type: 'switch', description: 'Coming soon' },
      ]
    },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    }}>
      {/* App Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: 'transparent',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 4 } }}>
          <IconButton
            onClick={() => navigate('/superadmin/dashboard')}
            sx={{ color: 'white', mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
          }}>
            <Settings sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, color: 'white' }}>
            System Configuration
          </Typography>

          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ px: { xs: 2, sm: 4 }, py: 4, maxWidth: 1200, mx: 'auto' }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, bgcolor: '#450a0a', color: '#fecaca', border: '1px solid #dc2626' }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3, bgcolor: '#052e16', color: '#bbf7d0', border: '1px solid #16a34a' }}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
            System Configuration
          </Typography>
          <Typography sx={{ color: '#94a3b8' }}>
            Manage global system settings and default configurations for all organizations.
          </Typography>
        </Box>

        {/* Config Sections */}
        <Stack spacing={3}>
          {configSections.map((section, sectionIndex) => (
            <Paper
              key={sectionIndex}
              sx={{
                bgcolor: '#1e293b',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <Box sx={{
                p: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderBottom: '1px solid rgba(255,255,255,0.1)',
              }}>
                <Box sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: `${section.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {React.cloneElement(section.icon, { sx: { color: section.color, fontSize: 24 } })}
                </Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  {section.title}
                </Typography>
              </Box>

              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {section.fields.map((field, fieldIndex) => (
                    <Grid item xs={12} sm={field.type === 'switch' ? 6 : 12} md={field.type === 'switch' ? 4 : 6} key={fieldIndex}>
                      {field.type === 'switch' ? (
                        <Box sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.08)',
                        }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(config[field.key])}
                                onChange={(e) => handleChange(field.key, e.target.checked)}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: section.color,
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: section.color,
                                  },
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography sx={{ color: 'white', fontWeight: 500 }}>
                                  {field.label}
                                </Typography>
                                {field.description && (
                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    {field.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                            labelPlacement="start"
                          />
                        </Box>
                      ) : (
                        <TextField
                          fullWidth
                          label={field.label}
                          type={field.type}
                          value={config[field.key]}
                          onChange={(e) => handleChange(field.key, field.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value)}
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: 'white',
                              '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                              '&.Mui-focused fieldset': { borderColor: section.color },
                            },
                            '& .MuiInputLabel-root': { color: '#94a3b8' },
                            '& .MuiInputLabel-root.Mui-focused': { color: section.color },
                          }}
                        />
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Paper>
          ))}
        </Stack>

        {/* Payment Gateway */}
        <Paper
          sx={{
            mt: 4,
            bgcolor: '#1e293b',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: 'rgba(16,185,129,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <AccountBalance sx={{ color: '#10b981', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  Payment Gateway
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  JazzCash integration credentials
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Alerts */}
          <Box sx={{ px: 3, pt: 2 }}>
            {paymentError && (
              <Alert
                severity="error"
                sx={{ mb: 2, bgcolor: '#450a0a', color: '#fecaca', border: '1px solid #dc2626' }}
                onClose={() => setPaymentError('')}
              >
                {paymentError}
              </Alert>
            )}
            {paymentSuccess && (
              <Alert
                severity="success"
                sx={{ mb: 2, bgcolor: '#052e16', color: '#bbf7d0', border: '1px solid #16a34a' }}
                onClose={() => setPaymentSuccess('')}
              >
                {paymentSuccess}
              </Alert>
            )}
          </Box>

          {/* Fields */}
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>

              {/* Enable toggle */}
              <Grid item xs={12} sm={6}>
                <Box sx={{
                  p: 2, borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(paymentConfig.jazzcash_enabled)}
                        onChange={(e) => handlePaymentChange('jazzcash_enabled', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10b981' },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ color: 'white', fontWeight: 500 }}>Enable JazzCash</Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Allow online payments via JazzCash
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                    labelPlacement="start"
                  />
                </Box>
              </Grid>

              {/* Sandbox toggle */}
              <Grid item xs={12} sm={6}>
                <Box sx={{
                  p: 2, borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(paymentConfig.jazzcash_sandbox_mode)}
                        onChange={(e) => handlePaymentChange('jazzcash_sandbox_mode', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#f59e0b' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#f59e0b' },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ color: 'white', fontWeight: 500 }}>Sandbox Mode</Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Use JazzCash test environment
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                    labelPlacement="start"
                  />
                </Box>
              </Grid>

              {/* Merchant ID */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Merchant ID"
                  value={paymentConfig.jazzcash_merchant_id}
                  onChange={(e) => handlePaymentChange('jazzcash_merchant_id', e.target.value)}
                  variant="outlined"
                  inputProps={{ autoComplete: 'off' }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#10b981' },
                    },
                    '& .MuiInputLabel-root': { color: '#94a3b8' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#10b981' },
                  }}
                />
              </Grid>

              {/* Return URL */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Return / Callback URL"
                  value={paymentConfig.jazzcash_return_url}
                  onChange={(e) => handlePaymentChange('jazzcash_return_url', e.target.value)}
                  variant="outlined"
                  placeholder="https://yourdomain.com/payment/return"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#10b981' },
                    },
                    '& .MuiInputLabel-root': { color: '#94a3b8' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#10b981' },
                  }}
                />
              </Grid>

              {/* Password */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Merchant Password"
                  type={showSecrets.jazzcash_password ? 'text' : 'password'}
                  value={paymentConfig.jazzcash_password}
                  onChange={(e) => handlePaymentChange('jazzcash_password', e.target.value)}
                  variant="outlined"
                  inputProps={{ autoComplete: 'new-password' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => toggleSecretVisibility('jazzcash_password')}
                          edge="end"
                          sx={{ color: '#64748b' }}
                        >
                          {showSecrets.jazzcash_password ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#10b981' },
                    },
                    '& .MuiInputLabel-root': { color: '#94a3b8' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#10b981' },
                  }}
                />
              </Grid>

              {/* Hash Key */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Hash Key (Integrity Salt)"
                  type={showSecrets.jazzcash_hash_key ? 'text' : 'password'}
                  value={paymentConfig.jazzcash_hash_key}
                  onChange={(e) => handlePaymentChange('jazzcash_hash_key', e.target.value)}
                  variant="outlined"
                  inputProps={{ autoComplete: 'new-password' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => toggleSecretVisibility('jazzcash_hash_key')}
                          edge="end"
                          sx={{ color: '#64748b' }}
                        >
                          {showSecrets.jazzcash_hash_key ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#10b981' },
                    },
                    '& .MuiInputLabel-root': { color: '#94a3b8' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#10b981' },
                  }}
                />
              </Grid>

            </Grid>

            {/* Save button */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={paymentSaving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                onClick={handleSavePayment}
                disabled={paymentSaving}
                sx={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
                  '&:disabled': { opacity: 0.6 },
                }}
              >
                {paymentSaving ? 'Saving...' : 'Save Payment Gateway'}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Danger Zone */}
        <Paper
          sx={{
            mt: 4,
            bgcolor: '#1e293b',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Box sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: '1px solid rgba(239,68,68,0.2)',
            bgcolor: 'rgba(239,68,68,0.1)',
          }}>
            <Typography variant="h6" sx={{ color: '#f87171', fontWeight: 600 }}>
              Danger Zone
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid rgba(239,68,68,0.2)',
                }}>
                  <Typography sx={{ color: 'white', fontWeight: 500, mb: 1 }}>
                    Reset All Settings
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                    This will reset all system settings to their default values.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => fetchConfig()}
                  >
                    Reset to Defaults
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid rgba(239,68,68,0.2)',
                }}>
                  <Typography sx={{ color: 'white', fontWeight: 500, mb: 1 }}>
                    Clear System Cache
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                    Clear all cached data. This may temporarily slow down the system.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Refresh />}
                  >
                    Clear Cache
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default SystemConfig;
